import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import AlertDialog from 'components/AlertDialog';
import Autocomplete from 'components/FormElements/AppAutocomplete';
import TextField from 'components/FormElements/AppTextInput';
import PageContainer from 'components/PageContainer';
import Table, { TableActions } from 'components/Table';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import {
  dateToLabel,
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { useDebounce } from 'usehooks-ts';

import { Delete, Edit, Email, Visibility } from '@mui/icons-material';
import { deleteAdviser } from '@redux/actions/Adviser';
import {
  ADVISER_ADD,
  createAdviserEditRoute,
  createAdviserViewRoute,
} from 'adminConstants/routes';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import TooltipTotalValue from 'components/TooltipTotalValue';
import usePlatformInvites from 'hooks/data/adviser/useAdviserPlatformInvites';
import { useAdvisers } from 'hooks/data/adviser/useAdvisers';
import api from 'lib/trpcClient';
import useApiRequestHandler from 'hooks/ui/useApiRequestHandler';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useNotification } from 'hooks/ui/useNotification';
import { canDeleteAdviser } from 'utils/adviserPermissions';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import { BackgroundExportTypes } from 'further-types/background-exports';
import { AdviserLink } from 'components/Links';

type Filters = {
  search?: string;
  organisationId?: {
    _id: string;
    name: string;
  } | null;
};

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'Advisers', isActive: true },
];

const Adviser: React.FC = () => {
  const dispatch = useDispatch();
  const { firmId, isSuperAdmin } = useGetRole();
  const [createPermission, deletePermission, editPermission] =
    useHasPermissions([
      RolePermission.CreateAdvisers,
      RolePermission.DeleteAdvisers,
      RolePermission.EditAdvisers,
    ]);

  const { success } = useNotification();

  const [disableDeleteBtn, setDisableDeleteBtn] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteDisclosure = useDisclosure();
  const handleApiRequest = useApiRequestHandler();

  const pagination = usePagination({
    orderBy: 'fullName',
    order: 'asc',
    id: 'advisers-list',
  });
  const { filters, handleChangeFilters } = useFilters<Filters>(
    'advisers-list',
    { onFiltersChange: pagination.toFirstPage },
  );

  const [search, setSearch] = useState(filters?.search || '');
  const debouncedSearch = useDebounce(search, 300);
  useEffect(() => {
    handleChangeFilters({ search: debouncedSearch });
  }, [debouncedSearch]);

  const params = {
    search: filters.search,
    organisationId: filters.organisationId?._id,
    order: pagination.order,
    orderBy: pagination.orderBy,
    firmId,
  };

  const advisers = useAdvisers({
    params: {
      page: pagination.page,
      perPage: pagination.rowsPerPage,
      ...params,
    },
  });

  const organisations = api.organisation.list.useQuery({
    firmId,
    order: 'asc',
    orderBy: 'name',
  });

  const { resendInvite } = usePlatformInvites({
    onResendInviteSuccess: () => {
      advisers.refetch();
      success('Adviser invitation sent successfully.');
    },
  });

  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const handleExport = () => {
    createBackgroundExport.mutate({
      exportType: BackgroundExportTypes.AdvisersExport,
      firmId: params.firmId,
      params: {
        organisationId: params.organisationId,
        search: debouncedSearch,
        order: pagination.order,
        orderBy: pagination.orderBy,
      },
    });
  };

  const columns = [
    {
      label: 'Name',
      key: 'fullName',
      render: (elm) => (
        <AdviserLink adviserId={elm?._id} adviserName={elm?.fullName} />
      ),
    },
    {
      label: 'Email',
      key: 'email',
    },
    {
      label: 'Organisation',
      key: 'organisationName',
      sort: false,
      render: (elm) => elm?.organisationId?.name,
    },
    {
      label: 'Clients',
      key: 'investorsCount',
      render: (elm) => numberToDisplayString(elm.investorsCount),
    },
    {
      label: 'Subscriptions',
      key: 'investmentsCount',
      render: (elm) => numberToDisplayString(elm.investmentsCount),
    },
    {
      label: 'Subscription value',
      key: 'initialValue',
      render: (elm) => numberToCurrencyString(elm.initialValue),
    },
    {
      label: <TooltipTotalValue />,
      key: 'currentValue',
      render: (elm) => numberToCurrencyString(elm.currentValue),
    },
    {
      label: 'Realised value',
      key: 'realisedValue',
      render: (elm) => numberToCurrencyString(elm.realisedValue),
    },
    {
      label: 'Advice fees',
      key: 'adviceFees',
      render: (elm) => numberToCurrencyString(elm.adviceFees),
    },
    {
      label: 'Last login',
      key: 'lastLogin',
      render: (elm) => dateToLabel(elm.lastLogin),
      sort: false,
    },
    {
      label: 'Actions',
      sort: false,
      key: 'actions',
      render: (adviser) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              label: 'View',
              icon: Visibility,
              color: 'primary',
              link: createAdviserViewRoute(adviser?._id),
            },
            {
              label: 'Edit',
              icon: Edit,
              color: 'primary',
              link: createAdviserEditRoute(adviser?._id),
              visible: editPermission,
            },
            {
              disabled: !canDeleteAdviser(
                isSuperAdmin,
                adviser,
                //@ts-expect-error
                firmId,
                deletePermission,
              ),
              tooltip: !canDeleteAdviser(
                isSuperAdmin,
                adviser,
                //@ts-expect-error
                firmId,
                deletePermission,
              )
                ? 'This adviser is linked with one or more subscriptions or more than one firm, so cannot be deleted.'
                : '',
              label: 'Delete',
              icon: Delete,
              color: 'error',
              onClick: () => deleteDisclosure.stageAction({ id: adviser._id }),
              visible: deletePermission,
            },
            {
              label: 'Resend Invite',
              icon: Email,
              color: 'primary',
              visible: editPermission,
              tooltip: isSuperAdmin
                ? 'Resending adviser invites is only possible for firm admin users.'
                : 'Resend invitation to this adviser.',
              disabled: isSuperAdmin || resendInvite.isPending,
              onClick: (e) => {
                e?.preventDefault();
                resendInvite.mutate(adviser._id);
              },
            },
          ]}
        />
      ),
    },
  ];

  const onDeleteAdviser = async () => {
    const adviserId = deleteDisclosure.actionProps.id;
    setIsDeleting(true);
    await handleApiRequest(async (showSuccess) => {
      const response = await dispatch<any>(deleteAdviser(adviserId));
      if (response) {
        showSuccess('Adviser deleted');
        deleteDisclosure.onClose();
        advisers.refetch();
      } else {
        setDisableDeleteBtn(true);
      }
    });
    setIsDeleting(false);
  };

  return (
    <PageContainer heading="Advisers" breadcrumbs={breadcrumbs}>
      <PageContent>
        <PageFilters
          pageId="advisers-list"
          buttons={[
            {
              label: 'Add Adviser',
              link: ADVISER_ADD,
              visible: createPermission,
            },
            {
              label: 'Export',
              loading: isExporting,
              onClick: handleExport,
            },
          ]}
        >
          <Filter>
            <TextField
              type="search"
              name="search"
              placeholder="Search by email, name, FCA number"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          </Filter>
          <Filter>
            <Autocomplete
              options={organisations?.data?.organisation ?? []}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option._id}
              filterSelectedOptions
              value={filters.organisationId}
              onChange={(_, newValue) => {
                handleChangeFilters({ organisationId: newValue });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select organisation"
                />
              )}
            />
          </Filter>
        </PageFilters>

        <Table
          columns={columns}
          onRequestSort={pagination.handleRequestSort}
          order={pagination.order}
          orderBy={pagination.orderBy}
          tablebody={advisers.data?.adviser ?? []}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          count={advisers.data?.totalAdviser ?? 0}
          pagination={true}
          loading={advisers.isFetching}
          emptyMessage="No advisers found"
          variant="nohover"
        />
      </PageContent>

      <AlertDialog
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        title="Are you sure you wish to delete this adviser?"
        content="This cannot be undone and all information relating to the adviser will be lost."
        btnLabels={{ confirm: 'Delete adviser', cancel: 'Go back' }}
        cancelBtnProps={{ variant: 'contained', color: 'primary' }}
        confirmBtnProps={{
          variant: 'outlined',
          color: 'primary',
          disabled: isDeleting || disableDeleteBtn,
        }}
        onConfirm={onDeleteAdviser}
      />
    </PageContainer>
  );
};
export default Adviser;
